import { ReactNode, useId } from "react";
import { Button } from "antd";
import { CardSkeleton, EmptyState } from "../../ui/States";
import styles from "./dash.module.css";

interface Props {
  title: string;
  helper: string;
  /** controls (toggles) rendered at the right of the heading */
  controls?: ReactNode;
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
  empty?: boolean;
  emptyTitle?: string;
  emptyText?: string;
  children: ReactNode;
}

/** One dashboard section: heading, helper sentence, and loading/error/empty states. */
export const DashCard = ({
  title, helper, controls, loading, error, onRetry, empty, emptyTitle = "Nothing to show yet", emptyText, children,
}: Props) => {
  const id = useId();
  let body: ReactNode = children;
  if (loading) body = <CardSkeleton height={140} />;
  else if (error)
    body = (
      <EmptyState
        tone="error"
        title={`Couldn't load ${title.toLowerCase()}`}
        description="The report service didn't respond. Your data is unchanged."
        actions={onRetry ? <Button onClick={onRetry}>Try again</Button> : undefined}
      />
    );
  else if (empty) body = <EmptyState title={emptyTitle} description={emptyText} />;

  return (
    <section className={styles.card} aria-labelledby={id}>
      <div className={styles.cardHead}>
        <div className={styles.cardHeadText}>
          <h2 id={id} className={styles.cardTitle}>{title}</h2>
          <p className={styles.cardHelper}>{helper}</p>
        </div>
        {controls && <div className={styles.cardControls}>{controls}</div>}
      </div>
      {body}
    </section>
  );
};
